import {
  Booking,
  ShipInterface,
  DurationInterface,
  CruiseDetailsInterface,
  BusInfoInterface,
  DriverInfoInterface,
  newGuideInterface,
  PosTimeInterface,
  StornoInterface,
  TourInterface,
  GuideDetailInterface,
  SourceMaps,
  UserInterface,
  CsvPreviewData,
  CsvImportError,
  TourDetailInterface,
} from './models';
import { Timestamp } from 'firebase/firestore'; // or wherever your Timestamp lives

import { v4 as uuidv4 } from 'uuid';
/**
 * Convert an array of CSV-derived records into fully-typed Booking objects.
 */
export function mapRawToBookings(
  rawData: Record<string, string>[],
  columns: string[],
  sourceMaps: SourceMaps,
  user: UserInterface,
): { bookings: Booking[]; errors: CsvImportError[] } {
  const bookings: Booking[] = [];
  const errors: CsvImportError[] = [];
  rawData.forEach((raw, index) => {
    try {
      // 1) Parse the core duration + tourcode
      // CSV date is “M/D/YYYY”
      //
      //
      // --- 1) Parse date/time safely ---
      const dateParts = parseDottedDate(raw['Booking Duration Datum']);
      const startTime = parseTimeHHMM(raw['Booking Duration Starttime']);
      const endTime = parseTimeHHMM(raw['Booking Duration Endtime']);

      if (!dateParts) {
        throw new Error('Invalid booking date (expected D.M.YYYY)');
      }
      if (!startTime) {
        throw new Error('Invalid start time (expected HH:MM)');
      }
      if (!endTime) {
        throw new Error('Invalid end time (expected HH:MM)');
      }

      const tsDate = buildUtcTimestamp(dateParts);
      const tsStart = buildUtcTimestamp(dateParts, startTime);
      const tsEnd = buildUtcTimestamp(dateParts, endTime);
      if (!tsDate || !tsStart || !tsEnd) {
        throw new Error('Failed to construct booking timestamps');
      }
      const duration: DurationInterface = {
        date: tsDate,
        startTime: tsStart,
        endTime: tsEnd,
        tourCode: (raw['Booking Duration Tourcode'] ?? '').trim(),
      };

      const loadedShip = sourceMaps.shipMap.get(raw['SchiffID']);
      let ship: ShipInterface = {
        id: '',
        companyName: 'N/A', // ← fill or look up
        shipName: 'N/A', // ← fill or look up
        createdDate: Timestamp.now(),
        lastEditDate: Timestamp.now(),
      };
      if (loadedShip) {
        // --- 2) Ship stub (you’ll likely want to look up name/company) ---
        ship = {
          id: loadedShip.id ?? '',
          companyName: loadedShip.companyName, // ← fill or look up
          shipName: loadedShip?.shipName, // ← fill or look up
          createdDate: loadedShip.createdDate ?? Timestamp.now(),
          lastEditDate: loadedShip.lastEditDate ?? Timestamp.now(),
        };
      }

      const jettyValidation = validateMapValue(
        sourceMaps.jettyMap,
        raw['Cruise Infos Anlegestelle'],
        raw['Nr'],
      );
      if (jettyValidation.error) {
        errors.push({
          row: index + 3,
          message: jettyValidation.error,
        });
      }
      // --- 3) Cruise details ---
      const cruiseDetails: CruiseDetailsInterface = {
        cruiseManager: raw['Cruise Infos Cruise Manager'] ?? '',
        cruiseManagerTel: '', // not in CSV
        jetty: jettyValidation.value,
        paxShip: parseInt(raw['Cruise Infos PAX Schiff'], 10),
        paxTour: parseInt(raw['Cruise Infos PAX Tour'], 10),
        internNote: '', //not in CSV,
        invoiceNumber: raw['Cruise Infos Rechnungsnr'] ?? '',
      };
      const busValidation = validateMapValue(
        sourceMaps.busCompanyMap,
        raw['Businfos Busfirma'],
        raw['Nr'],
      );

      if (busValidation.error) {
        errors.push({
          row: index + 3,
          message: busValidation.error,
        });
      }

      const toInt = (v: unknown): number => {
        const n = Number(String(v ?? '').replace(',', '.'));
        return Number.isFinite(n) ? n : 0;
      };

      const busInfos: BusInfoInterface = {
        busCompany: busValidation.value,
        selectedBuses: Object.fromEntries(
          Object.entries({
            '20er': toInt(raw['Businfos 20er']),
            '30er': toInt(raw['Businfos 30er']),
            '38': toInt(raw['Businfos 38']),
            '50': toInt(raw['Businfos 50']),
            '60': toInt(raw['Businfos 60']),
            taxi: toInt(raw['Businfos Taxi']),
            minivan: toInt(raw['Businfos Minivan']),
          }).filter(([, count]) => count > 0),
        ) as Record<string, number>,
      };
      // --- 5) Driver info ---
      const driverInfo: DriverInfoInterface = {
        url: raw['Lenker Infos LenkerInfo'] ?? '',
        customerNumber: Number(raw['Lenker Infos Kundenr'] ?? 0),
        orderNumber: Number(raw['Lenker Infos Aufstragsnr'] ?? 0),
        // drivers: {}    ← none provided
      };

      // --- 6) implementing Multiple guides in booking
      const extractedGuides: {
        guides: Record<string, newGuideInterface>;
        guideOrder: string[];
        errors?: CsvImportError[];
      } = extractGuides(raw, columns, sourceMaps, raw['Nr']);
      const guides = extractedGuides.guides;
      const guideOrder = extractedGuides.guideOrder;
      if (extractedGuides.errors?.length) {
        errors.push(...extractedGuides.errors);
      }

      // --- 7) Assemble the Booking ---
      const booking: Booking = {
        id: undefined,
        ship,
        duration,
        cruiseDetails,
        busInfos,
        driverInfo,
        guides,
        guideOrder,
        rundown: raw['Rundown'] ?? '',
        voucherAddonText: '',
        deleteFlag: false,
        createdDate: Timestamp.now(),
        userCreatorId: user.id,
        userCreatorName: user.username,
      };
      bookings.push(booking);
    } catch (err: any) {
      errors.push({
        row: index + 3,
        message: err.message ?? 'unknown error',
      });
    }
  });

  errors.sort((a, b) => a.row - b.row);
  return { bookings, errors };
}

/**
 * Helper to extract and build all guide objects from a CSV row.
 * @param row A single CSV-derived record with merged keys like "Guide 1 GuideID", "Guide 1 Ort", etc.
 * @param columns The list of all merged column keys.
 * @param baseDate The Date object representing the booking date (for time parsing).
 */

export function extractGuides(
  row: Record<string, string>,
  columns: string[],
  sourceMaps: SourceMaps,
  rowNum: string,
): {
  guides: Record<string, newGuideInterface>;
  guideOrder: string[];
  errors?: CsvImportError[];
} {
  const guides: Record<string, newGuideInterface> = {};
  const guideOrder: string[] = [];
  const errors: CsvImportError[] = [];

  const dateParts =
    parseDottedDate(row['Datum']) ??
    parseDottedDate(row['Booking Duration Datum']);

  const groupNames = Array.from(
    new Set(
      columns
        .filter((key) => key.startsWith('Guide '))
        .map((key) => key.split(' ').slice(0, 2).join(' ')),
    ),
  );

  for (const group of groupNames) {
    const getKey = (suffix: string) => `${group} ${suffix}`;
    const id = (row[getKey('GuideID')] ?? '').trim();
    if (!id) continue;

    // Parse Stellzeit
    const stellzeit = parseTimeHHMM(row[getKey('Stellzeit')]);
    const stellTimestamp =
      dateParts && stellzeit ? buildUtcTimestamp(dateParts, stellzeit) : null;

    const stornoValidation = validateMapValue(
      sourceMaps.stornoMap,
      row[getKey('Storno')],
      rowNum,
    );
    if (stornoValidation.error) {
      errors.push({ row: Number(rowNum), message: stornoValidation.error });
    }

    // 🔒 lookup guide detail safely
    const guideDetail = sourceMaps.guideMap.get(id);
    if (!guideDetail) {
      errors.push({
        row: Number(rowNum),
        message: `GuideID "${id}" not found in DB`,
      });
      // Skip this guide entry instead of crashing
      continue;
    }

    // create a fresh copy of languages with all false (immutability)
    const clearedLanguages = Object.fromEntries(
      Object.keys(guideDetail.languages ?? {}).map((code) => [code, false]),
    );

    // 3) Build Date objects *in UTC*

    const positionDetails: PosTimeInterface = {
      id,
      time: stellTimestamp ?? Timestamp.fromDate(new Date(0)), // or decide to skip if null

      location: row[getKey('Ort')] || '',
    };

    const storno: StornoInterface = {
      storno: stornoValidation.value,
      customStorno: '',
      isActive: Boolean(row[getKey('Storno')]),
    };

    // tour detail lookup

    const tourID = (row[getKey('TourID')] ?? '').trim();
    if (!tourID) continue;

    const loadedDetailTour = sourceMaps.tourMap.get(tourID);

    if (!loadedDetailTour) {
      errors.push({
        row: Number(rowNum),
        message: `TourID "${tourID}" not found in DB`,
      });
      // Skip this guide entry instead of crashing
      continue;
    }

    // const loadedDetailTour = Array.from(sourceMaps.tourMap.values()).find(
    //   (tour) => tour?.tourName === row[getKey('Tour')],
    // );
    const tourDetail: TourDetailInterface = loadedDetailTour
      ? {
          id: loadedDetailTour.id ?? '',
          tourName: loadedDetailTour.tourName ?? 'N/A',
          colorCode: loadedDetailTour.colorCode ?? '',
          createdDate: Timestamp.now(),
          lastEditDate: Timestamp.now(),
        }
      : {
          id: '',
          tourName: 'N/A',
          colorCode: '',
          createdDate: Timestamp.now(),
          lastEditDate: Timestamp.now(),
        };

    const tour: TourInterface = {
      id: '',
      tourInfo: tourDetail,
      tourLanguage: '',
      tourConfirm: false,
    };

    const other: Record<string, string> = {};
    for (const label of [
      'Sonstiges 1',
      'Sonstiges 2',
      'Sonstiges 3',
      'Sonstiges 4',
    ]) {
      const v = row[getKey(label)];
      if (v) other[label] = v;
    }

    const newGuide: newGuideInterface = {
      id,
      guideDetails: { ...guideDetail, languages: clearedLanguages },
      other,
      positionDetails,
      storno,
      tour,
    };

    guides[id] = newGuide;
    guideOrder.push(id);
  }

  return { guides, guideOrder, errors };
}

export function validateMapValue(
  sourceMap: Map<string, string> | { [key: string]: string },
  value: string,
  rowNum?: string,
): { value: string; error?: string } {
  const v = value ?? '';
  const validValues: string[] = Array.isArray((sourceMap as any).entries)
    ? Array.from((sourceMap as Map<string, string>).values())
    : Object.values(sourceMap as Record<string, string>);

  if (v && !validValues.includes(v)) {
    const errText = `${v} not found in DB!`;
    console.error(errText, rowNum ? `(Row ${rowNum})` : '');
    return { value: '', error: errText };
  }

  return { value: v };
}

export function isMeaningfulString(v: string | undefined): boolean {
  const PLACEHOLDERS = new Set([
    'n/a',
    'na',
    '-',
    '—',
    'null',
    'undefined',
    'unknown',
  ]);

  if (v) {
    const s = v.normalize().toLowerCase();
    return s.length > 0 && !PLACEHOLDERS.has(s);
  }
  return false;
}

export function mapToCsvPreviewBookings(bookings: Booking[]): CsvPreviewData[] {
  return bookings.map((booking, row) => {
    const errors: CsvImportError[] = [];

    if (!booking.duration?.date.seconds || booking.duration.date.seconds === 0)
      errors.push({ row: row + 1, message: 'Missing / Invalid date' });
    if (
      !booking.duration?.startTime.seconds ||
      booking.duration.startTime.seconds === 0
    )
      errors.push({ row: row + 1, message: 'Missing / Invalid start time' });
    if (
      !booking.duration?.endTime.seconds ||
      booking.duration.endTime.seconds === 0
    )
      errors.push({ row: row + 1, message: 'Missing / Invalid end time' });
    if (!isMeaningfulString(booking.duration.tourCode))
      errors.push({ row: row + 1, message: 'Missing / Invalid tour code' });
    if (!isMeaningfulString(booking.ship.shipName))
      errors.push({ row: row + 1, message: 'Missing / Invalid ship' });

    for (const key in booking.guides) {
      if (
        !booking.guides[key].positionDetails.time.seconds ||
        booking.guides[key].positionDetails.time.seconds === 0
      )
        errors.push({ row: row + 1, message: 'Missing / Invalid Stellzeit' });
      if (!isMeaningfulString(booking.guides[key].tour.tourInfo.tourName))
        errors.push({ row: row + 1, message: 'Missing / Tourname' });
    }

    const isValid = errors.length === 0;

    return {
      ...booking,
      id: uuidv4(),
      isValid,
      errors,
      selected: false, // optional default
    };
  });
}

export interface ParsedDateParts {
  year: number;
  month0: number; // 0-based
  day: number;
}

/** Accepts "D.M.YYYY" or "DD.MM.YYYY". Returns null if invalid. */
export function parseDottedDate(
  value: string | undefined | null,
): ParsedDateParts | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (
    !Number.isFinite(day) ||
    !Number.isFinite(month) ||
    !Number.isFinite(year)
  )
    return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month0: month - 1, day };
}

/** Accepts "HH:MM" (24h). Returns {hour, minute} or null. */
export function parseTimeHHMM(
  value: string | undefined | null,
): { hour: number; minute: number } | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/** Build a Firestore Timestamp in UTC from parts. Returns null if parts are invalid. */
export function buildUtcTimestamp(
  parts: ParsedDateParts,
  time?: { hour: number; minute: number },
): Timestamp | null {
  const h = time?.hour ?? 0;
  const min = time?.minute ?? 0;
  const d = new Date(
    Date.UTC(parts.year, parts.month0, parts.day, h, min, 0, 0),
  );
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
}
